import { useQuery } from "@tanstack/react-query";
import { Award, BookMarked, Briefcase, GraduationCap, Loader2, Sparkles } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { rpcClient } from "@/lib/rpc";

interface Period {
  year: number;
  month: number;
}

interface Position {
  companyName: string;
  title: string;
  description: string;
  employmentType: string;
  companyIndustry: string;
  companyStaffCountRange: string;
  start: Period;
  end: Period;
}

interface Education {
  schoolName: string;
  degree: string;
  fieldOfStudy: string;
  start: Period;
  end: Period;
}

interface AnonymizedProfile {
  fullName: string;
  headline: string;
  username: string | null;
  geo: { country: string | null; full: string | null };
  followerCount: number;
  connectionsCount: number;
  languages: { name: string; proficiency: string }[];
  educations: Education[];
  positions: Position[];
  skills: { name: string }[];
  certifications: { name: string; authority?: string }[];
}

interface FixtureProfileResponse {
  id: string;
  /** Raw fixture identifier — opaque token like `corpus-…` or
   *  `from-eval-<uuid>` / `[bad] from-eval-<uuid>` /
   *  `[pending] from-eval-<uuid>`. Used to extract the source
   *  evaluation ID for surfacing per-dimension correction rationales.
   *  NOT a real human name. */
  candidateName: string;
  displayName: string;
  hired: boolean;
  /** Tri-state CHRO decision. `pending` rows stay in the corpus list as
   *  a memory aid but are excluded from calibration runs. Optional for
   *  back-compat: legacy clients still receive `hired` only. */
  decision?: "hired" | "rejected" | "pending";
  /** Server-overlaid: latest CHRO correction wins over the seed value. */
  expectedScores: Record<string, number>;
  expectedSkills: Record<string, number>;
  /** Pre-correction values for keys whose value shifted; empty when nothing
   *  was adjusted. Drives the "old → new" comparison rendering. */
  originalScores?: Record<string, number>;
  originalSkills?: Record<string, number>;
  updatedAt: string;
  profile: AnonymizedProfile;
}

/** Subset of the corrections-audit row we render here. The full shape
 *  lives in `audit-section.tsx`; we keep this minimal — only fields used. */
interface AuditRow {
  id: string;
  evidenceId: string;
  targetKind: string;
  rationale: string;
  status: string;
  originalScore: string;
  correctedScore: string;
}

/**
 * Fixtures promoted from the candidate-intelligence flow embed the source
 * evaluation ID in their `candidateName` token (`from-eval-<uuid>` or
 * `[bad] from-eval-<uuid>`). Pure reference profiles use `corpus-…` and
 * return null — the corrections fetch is then skipped.
 */
function extractSourceEvaluationId(s: string | null | undefined): string | null {
  if (!s) return null;
  const m = /from-eval-([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i.exec(s);
  return m?.[1] ?? null;
}

const DIM_LABEL: Record<string, string> = {
  academic: "Acadêmico",
  trajectory: "Trajetória",
  tenure: "Estabilidade",
  founder: "Fundador",
};

// Both long-form (new promote-to-fixture) and short-form (legacy seed) keys
// resolve to the same human-readable label.
const SKILL_LABEL: Record<string, string> = {
  project_management: "Project Management",
  stakeholder_management: "Agency to influence stakeholders",
  business_acumen: "Business Acumen",
  hr_fluency: "HR Fluency",
  product_sense: "Product Sense",
  pm: "Project Management",
  sm: "Agency to influence stakeholders",
  ba: "Business Acumen",
  hf: "HR Fluency",
  ps: "Product Sense",
};

function fmtPeriod(p: Period): string {
  if (!p || (!p.year && !p.month)) return "";
  const m = p.month ? String(p.month).padStart(2, "0") : "";
  const y = p.year ? String(p.year) : "";
  return m && y ? `${m}/${y}` : y;
}

function fmtRange(start: Period, end: Period): string {
  const s = fmtPeriod(start);
  const e = fmtPeriod(end);
  if (!s && !e) return "—";
  if (!e) return `${s} – presente`;
  if (!s) return e;
  return `${s} – ${e}`;
}

function durationLabel(start: Period, end: Period): string | null {
  const sY = start?.year ?? 0;
  const sM = start?.month ?? 0;
  const eY = end?.year ?? new Date().getFullYear();
  const eM = end?.month ?? new Date().getMonth() + 1;
  if (!sY) return null;
  const months = (eY - sY) * 12 + (eM - sM);
  if (months <= 0) return null;
  if (months < 12) return `${months} mes${months === 1 ? "" : "es"}`;
  const y = Math.floor(months / 12);
  const m = months % 12;
  if (m === 0) return `${y} ano${y === 1 ? "" : "s"}`;
  return `${y}a ${m}m`;
}

export function CorpusProfileDrawer({
  fixtureId,
  open,
  onOpenChange,
}: {
  fixtureId: string | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const { data, isLoading, error } = useQuery<FixtureProfileResponse>({
    // biome-ignore lint/security/noSecrets: query key
    queryKey: ["labs", "calibrationCorpusGetProfile", fixtureId],
    queryFn: () => rpcClient.labs.calibrationCorpusGetProfile({ id: fixtureId! }),
    enabled: open && !!fixtureId,
  });

  const sourceEvaluationId = extractSourceEvaluationId(data?.candidateName);

  const { data: corrections } = useQuery<AuditRow[]>({
    queryKey: ["labs", "correctionsAuditList", { evaluationId: sourceEvaluationId, legacy: true }],
    enabled: Boolean(sourceEvaluationId),
    queryFn: () =>
      rpcClient.labs.correctionsAuditList({
        evaluationId: sourceEvaluationId as string,
        limit: 200,
        legacy: true,
      }),
  });

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-[620px] overflow-y-auto">
        <SheetHeader className="space-y-2">
          <div className="flex items-center gap-3">
            <SheetTitle className="text-2xl font-bold tracking-tight">
              {data?.displayName ?? "Carregando…"}
            </SheetTitle>
            {data &&
              (() => {
                const decision = data.decision ?? (data.hired ? "hired" : "rejected");
                if (decision === "pending") {
                  return (
                    <span
                      className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded"
                      style={{ background: "rgba(120,113,108,0.12)", color: "#57534e" }}
                      title="Decisão pendente — perfil fica fora das calibrações até você decidir contratado/não contratado"
                    >
                      pendente
                    </span>
                  );
                }
                const hired = decision === "hired";
                return (
                  <span
                    className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded"
                    style={{
                      background: hired ? "rgba(15,44,89,0.08)" : "rgba(244,54,76,0.08)",
                      color: hired ? "#0f2c59" : "#f4364c",
                    }}
                  >
                    {hired ? "contratamos" : "não contratamos"}
                  </span>
                );
              })()}
          </div>
          <SheetDescription className="text-xs text-muted-foreground">
            Dossiê completo — a superfície de calibração é interna, então mantemos nome, headline e
            histórico como o modelo realmente lê.
          </SheetDescription>
        </SheetHeader>

        {isLoading && (
          <div className="flex justify-center py-20">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {error && (
          <p className="py-8 text-sm text-rose-600">
            Não consegui carregar o perfil: {(error as Error).message}
          </p>
        )}

        {data && <ProfileBody data={data} corrections={corrections} />}
      </SheetContent>
    </Sheet>
  );
}

function ProfileBody({
  data,
  corrections,
}: {
  data: FixtureProfileResponse;
  corrections?: AuditRow[];
}) {
  const { profile, expectedScores, expectedSkills } = data;
  const originalScores = data.originalScores ?? {};
  const originalSkills = data.originalSkills ?? {};
  // Per-dim rationales: only `targetKind === "dim"` corrections that weren't
  // rejected. We index by evidenceId so each tile can pick up its match.
  const dimCorrections = new Map<string, AuditRow>();
  // Per-skill rationales: skill corrections (`targetKind` =
  // `skill_framework` or `skill_leveling`) carry the framework skill id in
  // `evidenceId`. Stored expectedSkills can be keyed in the legacy short
  // form (`pm`, `sm`, ...) or the long form (`project_management`, ...) —
  // index by both so a tile keyed in either shape finds its match.
  const skillCorrections = new Map<string, AuditRow>();
  const SKILL_SHORT_TO_LONG: Record<string, string> = {
    pm: "project_management",
    sm: "stakeholder_management",
    ba: "business_acumen",
    hf: "hr_fluency",
    ps: "product_sense",
  };
  const SKILL_LONG_TO_SHORT: Record<string, string> = Object.fromEntries(
    Object.entries(SKILL_SHORT_TO_LONG).map(([s, l]) => [l, s]),
  );
  if (corrections) {
    for (const c of corrections) {
      if (c.status === "rejected") continue;
      if (c.targetKind === "dim") {
        if (!dimCorrections.has(c.evidenceId)) dimCorrections.set(c.evidenceId, c);
      } else if (c.targetKind === "skill_framework" || c.targetKind === "skill_leveling") {
        const longKey = SKILL_SHORT_TO_LONG[c.evidenceId] ?? c.evidenceId;
        const shortKey = SKILL_LONG_TO_SHORT[c.evidenceId] ?? c.evidenceId;
        // First non-rejected correction per skill wins (same descending-time
        // ordering as the dim path). Index under both shapes so the tile
        // keyed in either form finds its match.
        if (!skillCorrections.has(longKey)) skillCorrections.set(longKey, c);
        if (!skillCorrections.has(shortKey)) skillCorrections.set(shortKey, c);
      }
    }
  }
  return (
    <div className="mt-6 space-y-8 pb-12">
      <HeaderStrip profile={profile} />

      {profile.positions.length > 0 && (
        <Section icon={Briefcase} title="Carreira" count={profile.positions.length}>
          <ol className="relative border-l pl-5 space-y-5 ml-1">
            {profile.positions
              .slice()
              .reverse()
              .map((p, i) => (
                <li
                  // biome-ignore lint/suspicious/noArrayIndexKey: structural position order is stable within a fetched profile
                  key={`pos-${i}`}
                  className="relative"
                >
                  <span className="absolute -left-[29px] top-1.5 h-2 w-2 rounded-full bg-primary" />
                  <div className="space-y-1">
                    <div className="flex items-baseline justify-between gap-3 flex-wrap">
                      <h4 className="font-semibold leading-tight">{p.title || "—"}</h4>
                      <span className="text-[11px] font-mono tabular-nums text-muted-foreground">
                        {fmtRange(p.start, p.end)}
                        {durationLabel(p.start, p.end) ? ` · ${durationLabel(p.start, p.end)}` : ""}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {p.companyName}
                      {p.companyIndustry ? ` · ${p.companyIndustry}` : ""}
                      {p.companyStaffCountRange ? ` · ${p.companyStaffCountRange}` : ""}
                    </p>
                    {p.description && (
                      <p className="text-sm leading-relaxed text-foreground/80 whitespace-pre-wrap">
                        {p.description}
                      </p>
                    )}
                  </div>
                </li>
              ))}
          </ol>
        </Section>
      )}

      {profile.educations.length > 0 && (
        <Section icon={GraduationCap} title="Formação" count={profile.educations.length}>
          <ul className="space-y-3">
            {profile.educations.map((e, i) => (
              <li
                // biome-ignore lint/suspicious/noArrayIndexKey: structural education order is stable within a fetched profile
                key={`edu-${i}`}
                className="text-sm"
              >
                <div className="flex items-baseline justify-between gap-3 flex-wrap">
                  <span className="font-semibold">{e.schoolName || "—"}</span>
                  <span className="text-[11px] font-mono tabular-nums text-muted-foreground">
                    {fmtRange(e.start, e.end)}
                  </span>
                </div>
                <p className="text-muted-foreground">
                  {[e.degree, e.fieldOfStudy].filter(Boolean).join(" · ") || "—"}
                </p>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {profile.skills.length > 0 && (
        <Section icon={Sparkles} title="Skills auto-declaradas" count={profile.skills.length}>
          <div className="flex flex-wrap gap-1.5">
            {profile.skills.map((s, i) => (
              <span
                // biome-ignore lint/suspicious/noArrayIndexKey: skill list order is stable within a fetched profile
                key={`skill-${i}`}
                className="text-[11px] leading-none px-2 py-1 rounded border border-border text-foreground/70 bg-background"
              >
                {s.name}
              </span>
            ))}
          </div>
        </Section>
      )}

      {profile.certifications.length > 0 && (
        <Section icon={Award} title="Certificações" count={profile.certifications.length}>
          <ul className="space-y-1 text-sm">
            {profile.certifications.map((c, i) => (
              <li
                // biome-ignore lint/suspicious/noArrayIndexKey: cert list order is stable within a fetched profile
                key={`cert-${i}`}
                className="text-foreground/80"
              >
                <span className="font-medium">{c.name}</span>
                {c.authority ? (
                  <span className="text-muted-foreground"> · {c.authority}</span>
                ) : null}
              </li>
            ))}
          </ul>
        </Section>
      )}

      {Object.keys(expectedScores).length > 0 && (
        <Section icon={BookMarked} title="Notas esperadas · evidências">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {Object.entries(expectedScores).map(([dim, score]) => {
              const correction = dimCorrections.get(dim);
              const original = originalScores[dim];
              const hasDiff = typeof original === "number" && original !== Number(score);
              return (
                <div key={dim} className="rounded border p-2 flex flex-col gap-0.5 bg-muted/20">
                  <div className="flex items-center justify-between gap-1">
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      {DIM_LABEL[dim] ?? dim}
                    </span>
                    {(correction || hasDiff) && (
                      <span
                        className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800"
                        data-testid={`dim-corrected-${dim}`}
                      >
                        ✎ ajustada
                      </span>
                    )}
                  </div>
                  <div className="flex items-baseline gap-1.5">
                    {hasDiff && (
                      <span
                        className="font-mono tabular-nums text-xs text-muted-foreground line-through"
                        data-testid={`dim-original-${dim}`}
                      >
                        {original.toFixed(1)}
                      </span>
                    )}
                    <span className="font-mono tabular-nums font-semibold">
                      {Number(score).toFixed(1)}
                    </span>
                  </div>
                  {correction?.rationale && (
                    <q
                      className="text-[11px] italic text-foreground/70 mt-1 leading-snug"
                      data-testid={`dim-rationale-${dim}`}
                    >
                      {correction.rationale}
                    </q>
                  )}
                </div>
              );
            })}
          </div>
        </Section>
      )}

      {Object.keys(expectedSkills ?? {}).length > 0 && (
        <Section icon={Sparkles} title="Nível esperado · skills">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {Object.entries(expectedSkills).map(([skillId, dots]) => {
              const correction = skillCorrections.get(skillId);
              const displayDots = Number(dots);
              const original = originalSkills[skillId];
              const hasDiff = typeof original === "number" && original !== displayDots;
              return (
                <div key={skillId} className="rounded border p-2 flex flex-col gap-0.5 bg-muted/20">
                  <div className="flex items-center justify-between gap-1">
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      {SKILL_LABEL[skillId] ?? skillId}
                    </span>
                    {(correction || hasDiff) && (
                      <span
                        className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800"
                        data-testid={`skill-corrected-${skillId}`}
                      >
                        ✎ ajustada
                      </span>
                    )}
                  </div>
                  <div className="flex items-baseline gap-1.5">
                    {hasDiff && (
                      <span
                        className="font-mono tabular-nums text-xs text-muted-foreground line-through"
                        data-testid={`skill-original-${skillId}`}
                      >
                        {Math.round(original)}
                      </span>
                    )}
                    <span className="font-mono tabular-nums font-semibold">
                      {Math.round(displayDots)}{" "}
                      <span className="text-muted-foreground text-xs">/ 5</span>
                    </span>
                  </div>
                  {correction?.rationale && (
                    <q
                      className="text-[11px] italic text-foreground/70 mt-1 leading-snug"
                      data-testid={`skill-rationale-${skillId}`}
                    >
                      {correction.rationale}
                    </q>
                  )}
                </div>
              );
            })}
          </div>
          <p className="text-[11px] text-muted-foreground mt-2">
            Alvos de nível (pontos) que o agente de calibração tenta preservar para este perfil.
            Editáveis na tabela do corpus.
          </p>
        </Section>
      )}
    </div>
  );
}

function HeaderStrip({ profile }: { profile: AnonymizedProfile }) {
  const items: { k: string; v: string }[] = [];
  if (profile.headline) items.push({ k: "headline", v: profile.headline });
  if (profile.geo?.full) items.push({ k: "localização", v: profile.geo.full });
  else if (profile.geo?.country) items.push({ k: "país", v: profile.geo.country });
  if (profile.username) items.push({ k: "linkedin", v: profile.username });
  if (profile.connectionsCount > 0) {
    items.push({ k: "conexões", v: profile.connectionsCount.toLocaleString("pt-BR") });
  }
  if (profile.followerCount > 0) {
    items.push({ k: "seguidores", v: profile.followerCount.toLocaleString("pt-BR") });
  }
  if (profile.languages.length > 0) {
    items.push({ k: "idiomas", v: profile.languages.map((l) => l.name).join(", ") });
  }
  if (items.length === 0) return null;
  return (
    <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3 border-y py-3">
      {items.map((it) => (
        <div key={it.k} className="flex flex-col gap-0.5 min-w-0">
          <dt className="text-[10px] uppercase tracking-wider text-muted-foreground">{it.k}</dt>
          <dd className="text-sm font-medium truncate">{it.v}</dd>
        </div>
      ))}
    </dl>
  );
}

function Section({
  icon: Icon,
  title,
  count,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  count?: number;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-3">
      <header className="flex items-baseline gap-2 border-b pb-2">
        <Icon className="h-4 w-4 text-primary" />
        <h3 className="text-sm font-bold uppercase tracking-[0.14em]">{title}</h3>
        {count !== undefined && (
          <span className="text-[11px] font-mono tabular-nums text-muted-foreground">
            · {count}
          </span>
        )}
      </header>
      {children}
    </section>
  );
}
